import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  Fade,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';

const colors = {
  primary: '#5AB9D3',
  primaryDark: '#4AA5C0',
  secondary: '#101A3A',
  secondaryLight: '#1A2747',
  backgroundLight: '#F8FAFC',
  textDark: '#2D3748',
  textLight: '#718096',
  error: '#E53E3E',
  success: '#38A169',
  white: '#FFFFFF'
};

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: ${colors.backgroundLight};
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
  animation: fadeIn 0.8s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: ${colors.white};
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.8s ease-in-out;

  @media (max-width: 900px) {
    display: none;
  }
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.primary};
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 3;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  color: ${colors.secondary};
  margin-bottom: 0.5rem;
  font-weight: 700;
  background: linear-gradient(to right, ${colors.primary}, ${colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.h2`
  font-size: 1rem;
  color: ${colors.textLight};
  margin-bottom: 2rem;
  font-weight: 400;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.2rem;
`;

const StyledTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 12px;
      background-color: rgba(255, 255, 255, 0.9);
      
      &:hover fieldset {
        border-color: ${colors.primary};
      }
      
      &.Mui-focused fieldset {
        border-color: ${colors.primary};
        border-width: 2px;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  && {
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    background: linear-gradient(to right, ${colors.primary}, ${colors.secondary});
    color: ${colors.white};
    height: 48px;
    
    &:hover {
      background: linear-gradient(to right, ${colors.primaryDark}, ${colors.secondaryLight});
    }
  }
`;

const BackToLogin = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
  font-weight: 500;
`;

export default function ResetPassword() {
  const { token } = useParams(); // Captura o token da URL
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalSuccess, setModalSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setModalMessage('Link inválido ou expirado. Por favor, solicite um novo link de redefinição.');
      setModalSuccess(false);
      setModalOpen(true);
    }
  }, [token]);

  const handleCloseModal = () => {
    setModalOpen(false);
    if (modalSuccess) {
      navigate('/login');
    }
  };

  const onSubmit = async (data) => {
    if (data.newPassword !== data.confirmationPassword) {
      setModalMessage('As senhas não coincidem.');
      setModalSuccess(false);
      setModalOpen(true);
      return;
    }

    if (!token) {
      setModalMessage('Token inválido. Solicite um novo link de redefinição.');
      setModalSuccess(false);
      setModalOpen(true);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        token: token, // Usando o token capturado da URL
        newPassword: data.newPassword,
        confirmationPassword: data.confirmationPassword
      };

      console.log('Enviando payload:', payload); // Para debug

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/reset-password`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setModalMessage('Senha redefinida com sucesso! Você já pode fazer login com sua nova senha.');
      setModalSuccess(true);
    } catch (error) {
      console.error('Erro na requisição:', error); // Para debug
      const message = error.response?.data?.message || 'Erro ao redefinir senha. Tente novamente.';
      setModalMessage(message);
      setModalSuccess(false);
    } finally {
      setModalOpen(true);
      setLoading(false);
    }
  };

  return (
    <Container>
      <LeftSection>
        <Inner>
          <BrandLogo>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z" fill="url(#paint0_linear)"/>
              <path d="M22.4 12.8C22.4 14.92 20.52 16.8 18.4 16.8C16.28 16.8 14.4 14.92 14.4 12.8C14.4 10.68 16.28 8.8 18.4 8.8C20.52 8.8 22.4 10.68 22.4 12.8Z" fill="white"/>
              <path d="M9.6 19.2C9.6 21.32 7.72 23.2 5.6 23.2C3.48 23.2 1.6 21.32 1.6 19.2C1.6 17.08 3.48 15.2 5.6 15.2C7.72 15.2 9.6 17.08 9.6 19.2Z" fill="white"/>
              <path d="M26.4 19.2C26.4 21.32 24.52 23.2 22.4 23.2C20.28 23.2 18.4 21.32 18.4 19.2C18.4 17.08 20.28 15.2 22.4 15.2C24.52 15.2 26.4 17.08 26.4 19.2Z" fill="white"/>
              <defs>
                <linearGradient id="paint0_linear" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                  <stop stopColor={colors.primary}/>
                  <stop offset="1" stopColor={colors.secondary}/>
                </linearGradient>
              </defs>
            </svg>
            Realoca Fácil
          </BrandLogo>

          <Header>
            <Title>Redefinir Senha</Title>
            <Subtitle>Digite sua nova senha nos campos abaixo</Subtitle>
          </Header>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledTextField
              label="Nova Senha"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('newPassword', { 
                required: 'Campo obrigatório',
                minLength: { value: 6, message: 'Mínimo 6 caracteres' }
              })}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
            />

            <StyledTextField
              label="Confirmar Senha"
              type={showConfirmationPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmationPassword(!showConfirmationPassword)} edge="end">
                      {showConfirmationPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('confirmationPassword', { 
                required: 'Campo obrigatório',
                validate: value => value === watch('newPassword') || 'Senhas não coincidem'
              })}
              error={!!errors.confirmationPassword}
              helperText={errors.confirmationPassword?.message}
            />

            <StyledButton
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Redefinir Senha'}
            </StyledButton>

            <BackToLogin href="/login">Voltar para o login</BackToLogin>
          </Form>
        </Inner>
      </LeftSection>

      <RightSection>
        <Typography variant="h4" gutterBottom style={{ fontWeight: 600, color: colors.white }}>
          Transforme sua gestão de clientes
        </Typography>
        <Typography variant="body1" style={{ color: 'rgba(255,255,255,0.9)' }}>
          A plataforma completa para otimizar seus processos e aumentar sua produtividade.
        </Typography>
      </RightSection>

      <Modal open={modalOpen} onClose={handleCloseModal} closeAfterTransition>
        <Fade in={modalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: modalSuccess ? '#E6FFFA' : '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              {modalSuccess ? (
                <Lock color="success" fontSize="large" />
              ) : (
                <Lock color="error" fontSize="large" />
              )}
            </Box>
            <Typography variant="h6" gutterBottom>
              {modalSuccess ? 'Sucesso!' : 'Erro'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {modalMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseModal}
              fullWidth
              sx={{ mt: 2 }}
              style={{ background: modalSuccess ? colors.success : colors.primary }}
            >
              Entendi
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
}